import { ButtonHTMLAttributes, ReactNode, Ref, forwardRef } from 'react';

import { cn } from '@foundation/utils/cn';

import { LoadingSpinner } from '../../LoadingSpinner';

interface IPrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly size?: 'sm' | 'md' | 'lg';
  readonly variant?: 'primary' | 'secondary' | 'default' | 'light';
  readonly isLoading?: boolean;
  readonly children: ReactNode;
}

function PrimaryButtonComponent(
  props: IPrimaryButtonProps,
  ref: Ref<HTMLButtonElement>,
) {
  const {
    children,
    size = 'md',
    variant = 'primary',
    className,
    isLoading,
    ...buttonProps
  } = props;

  return (
    <button
      type="button"
      {...buttonProps}
      className={cn(
        'cursor-pointer rounded-full disabled:cursor-not-allowed disabled:opacity-20 disabled:shadow-none grid grid-cols-1 grid-rows-1 place-items-center',
        {
          'h-8 text-xs px-2 py-1': size === 'sm',
          'h-14 px-8 py-3': size === 'md',
          'text-xl px-10 py-4 h-20': size === 'lg',
          'bg-brand hover:bg-brand-600 text-white shadow-md':
            variant === 'primary',
          'text-brand px-0 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2':
            variant === 'default',
          'border border-primary': variant === 'secondary',
          'bg-brand-50 hover:bg-gray-200 text-brand shadow-md':
            variant === 'light',
        },
        className,
      )}
      ref={ref}
    >
      {isLoading && (
        <div className="col-start-1 row-start-1">
          <LoadingSpinner
            smallSize
            variant={variant === 'primary' ? 'white' : 'default'}
          />
        </div>
      )}
      <span
        className={cn(
          'col-start-1 row-start-1 flex flex-row justify-center items-center flex-nowrap',
          {
            invisible: isLoading,
          },
        )}
      >
        {children}
      </span>
    </button>
  );
}

export const PrimaryButton = forwardRef(PrimaryButtonComponent);
